import { useEffect, useRef } from "react";

export function useTileProgressTracker(
  map,
  mapLoaded,
  setTileProgress,
  setTileTelemetry
) {

  const attachedRef = useRef(false);

  useEffect(() => {

    if (!map || !mapLoaded) {
      return;
    }

    if (attachedRef.current) return;

    attachedRef.current = true;

    const pending = new Set();
    const loaded = new Set();
    const tileStartTimes = new Map();

    let rateWindowStart = performance.now();
    let rateLoaded = 0;

    function getTileId(e) {
      if (!e.tile) return null;

      return (
        e.tile.uid ||
        `${e.tile.tileID?.z}/${e.tile.tileID?.x}/${e.tile.tileID?.y}`
      );
    }

    function updateHUD() {

      const total = pending.size + loaded.size;

      if (total === 0) {
        setTileProgress({ active: false, total: 0, loaded: 0 });
        return;
      }

      const now = performance.now();
      const elapsed = (now - rateWindowStart) / 1000;

      if (elapsed >= 1) {

        setTileTelemetry(t => ({
          ...t,
          tilesPerSec: rateLoaded / elapsed
        }));

        rateWindowStart = now;
        rateLoaded = 0;

      }

      setTileProgress({
        active: true,
        total,
        loaded: loaded.size
      });

    }

    function onTileStart(e) {

      if (!e.tile || !e.sourceId?.startsWith("src-")) return;

      const id = getTileId(e);
      if (!id) return;

      if (!pending.has(id) && !loaded.has(id)) {

        pending.add(id);
        tileStartTimes.set(id, performance.now());

        updateHUD();

      }

    }

    function onTileComplete(e) {

      if (!e.tile || !e.sourceId?.startsWith("src-")) return;

      const id = getTileId(e);
      if (!id) return;

      if (pending.has(id)) {

        pending.delete(id);
        loaded.add(id);

        rateLoaded++;

        updateHUD();

      }

      const start = tileStartTimes.get(id);

      if (start) {

        const latency = performance.now() - start;

        tileStartTimes.delete(id);

        setTileTelemetry(t => ({
          ...t,
          latencySum: t.latencySum + latency,
          latencyCount: t.latencyCount + 1
        }));

      }

    }

    function onMoveStart() {

      pending.clear();
      loaded.clear();
      tileStartTimes.clear();

      rateLoaded = 0;
      rateWindowStart = performance.now();

      setTileProgress({ active: false, total: 0, loaded: 0 });

    }

    function onIdle() {

      const total = pending.size + loaded.size;

      if (total > 0) {

        setTileProgress({
          active: true,
          total,
          loaded: total
        });

      }

      // small delay so the bar visually reaches 100%
      setTimeout(() => {

        pending.clear();
        loaded.clear();
        tileStartTimes.clear();

        setTileProgress({
          active: false,
          total: 0,
          loaded: 0
        });

      }, 150);
    }

    map.on("idle", onIdle);
    map.on("sourcedataloading", onTileStart);
    map.on("sourcedata", onTileComplete);
    map.on("movestart", onMoveStart);

    return () => {

      map.off("sourcedataloading", onTileStart);
      map.off("sourcedata", onTileComplete);
      map.off("movestart", onMoveStart);
      map.off("idle", onIdle);

    };

  }, [mapLoaded, map]);

}